import type { IntakeSpec, ArchitectureBuildingDetails } from '@shared/types';
import type { Part, Joint, Feature, WallPart, WallOpening } from '@shared/spec';

function getDetails(intake: IntakeSpec): ArchitectureBuildingDetails {
  return intake.details as ArchitectureBuildingDetails;
}

function scaleNumber(scaleRatio: string): number {
  // e.g., '1:100' -> 100
  return parseInt(scaleRatio.split(':')[1], 10);
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const scale = scaleNumber(d.scaleRatio);

  // Real-world dimensions in mm
  const realLength = intake.core.size?.length ?? 10000; // 10m default
  const realWidth = intake.core.size?.width ?? 8000;    // 8m default
  const floorHeight = 3000; // 3m per floor
  const wallThickness = 3;  // model material thickness, NOT scaled

  const modelLength = realLength / scale;
  const modelWidth = realWidth / scale;
  const modelFloorHeight = floorHeight / scale;

  // Window openings per exterior wall: 2 windows per floor
  function makeWindowOpenings(count: number): WallOpening[] {
    const openings: WallOpening[] = [];
    const winWidth = 1200 / scale;
    const winHeight = 1200 / scale;
    const winZ = 1000 / scale;
    for (let i = 0; i < count; i++) {
      const spacing = (count + 1);
      const xOffset = ((i + 1) / spacing - 0.5) * modelLength;
      openings.push({
        kind: 'window',
        x: xOffset,
        z: winZ,
        width: winWidth,
        height: winHeight,
      });
    }
    return openings;
  }

  function makeEastWestWindowOpenings(count: number): WallOpening[] {
    const openings: WallOpening[] = [];
    const winWidth = 1200 / scale;
    const winHeight = 1200 / scale;
    const winZ = 1000 / scale;
    for (let i = 0; i < count; i++) {
      const spacing = (count + 1);
      const xOffset = ((i + 1) / spacing - 0.5) * modelWidth;
      openings.push({
        kind: 'window',
        x: xOffset,
        z: winZ,
        width: winWidth,
        height: winHeight,
      });
    }
    return openings;
  }

  const out: Part[] = [];

  // Perimeter walls for each floor
  for (let f = 1; f <= d.floorCount; f++) {
    const baseZ = (f - 1) * modelFloorHeight;

    // North wall
    const northWall: WallPart = {
      id: `floor${f}_wall_north`,
      name: `${f}階 北壁`,
      kind: 'wall',
      materialId: 'mat-primary',
      width: modelLength,
      height: modelFloorHeight,
      thickness: wallThickness,
      position: [0, modelWidth / 2, baseZ],
      rotation: [0, 0, 0],
      tags: ['exterior'],
      openings: makeWindowOpenings(2),
      paramRefs: [],
    };
    out.push(northWall);

    // South wall
    const southWall: WallPart = {
      id: `floor${f}_wall_south`,
      name: `${f}階 南壁`,
      kind: 'wall',
      materialId: 'mat-primary',
      width: modelLength,
      height: modelFloorHeight,
      thickness: wallThickness,
      position: [0, -(modelWidth / 2), baseZ],
      rotation: [0, 0, 0],
      tags: ['exterior'],
      openings: makeWindowOpenings(2),
      paramRefs: [],
    };
    out.push(southWall);

    // East wall
    const eastWall: WallPart = {
      id: `floor${f}_wall_east`,
      name: `${f}階 東壁`,
      kind: 'wall',
      materialId: 'mat-primary',
      width: modelWidth,
      height: modelFloorHeight,
      thickness: wallThickness,
      position: [modelLength / 2, 0, baseZ],
      rotation: [0, 0, 90],
      tags: ['exterior'],
      openings: makeEastWestWindowOpenings(2),
      paramRefs: [],
    };
    out.push(eastWall);

    // West wall
    const westWall: WallPart = {
      id: `floor${f}_wall_west`,
      name: `${f}階 西壁`,
      kind: 'wall',
      materialId: 'mat-primary',
      width: modelWidth,
      height: modelFloorHeight,
      thickness: wallThickness,
      position: [-(modelLength / 2), 0, baseZ],
      rotation: [0, 0, 90],
      tags: ['exterior'],
      openings: makeEastWestWindowOpenings(2),
      paramRefs: [],
    };
    out.push(westWall);
  }

  // Roof panel
  // TODO: sloped roof per roofStyle, V2
  out.push({
    id: 'roof',
    name: '屋根',
    kind: 'panel',
    materialId: 'mat-primary',
    width: modelLength,
    depth: modelWidth,
    thickness: wallThickness,
    position: [0, 0, d.floorCount * modelFloorHeight],
    rotation: [0, 0, 0],
    tags: ['roof'],
    paramRefs: [],
    cutouts: [],
  });

  // Ground floor interior panel
  if (d.showInterior) {
    out.push({
      id: 'floor_ground',
      name: '1階床',
      kind: 'panel',
      materialId: 'mat-primary',
      width: modelLength,
      depth: modelWidth,
      thickness: wallThickness,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['floor'],
      paramRefs: [],
      cutouts: [],
    });
  }

  // Landscape ground panel
  if (d.showLandscape) {
    const landscapeLength = (realLength + 1000) / scale;
    const landscapeWidth = (realWidth + 1000) / scale;
    out.push({
      id: 'landscape',
      name: '地面',
      kind: 'panel',
      materialId: 'mat-primary',
      width: landscapeLength,
      depth: landscapeWidth,
      thickness: wallThickness,
      position: [0, 0, -wallThickness],
      rotation: [0, 0, 0],
      tags: ['landscape'],
      paramRefs: [],
      cutouts: [],
    });
  }

  return out;
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(_intake: IntakeSpec): Feature[] {
  return [];
}
